import { Capacitor } from '@capacitor/core'
import { Box, SxProps, Theme } from '@mui/material'
import { useWindowHeight, useWindowWidth } from '@react-hook/window-size'
import merge from 'lodash/merge'
import { forwardRef, ReactNode, useEffect, useRef, useState } from 'react'
import { isIOS } from 'react-device-detect'

import { styles, styleSets } from '@/components/styles'
import { constants } from '@/config'
import { useRouter } from '@/core'
import { CONSOLE_HEADER, useSafeAreaSetting } from '@/lib/safeAreaView'

import { defaultTheme } from '../../styles/themes/defaultTheme'
import { ArtistSideMenuForPC } from '../ArtistSideMenu'
import ConsoleFooter from '../ConsoleFooter'
import { ConsoleHeader } from '../ConsoleHeader'
import { HeaderProps } from '../Header'

export const ConsoleBaseLayout = forwardRef<
  HTMLDivElement,
  ConsoleBaseLayoutProps
>(
  (
    {
      children,
      header,
      margin,
      sx,
      showScrollBar = false,
      customNavigation,
      customNavigationSx,
      safeArea,
      black,
      hasConsoleFooter,
      // 全コンソール画面のレスポンシブ対応が完了したら
      // 1. ConsoleBaseLayout から fullWidth そのものを削除
      // 2. ConsoleBaseLayout 内の CSS 条件分岐を削除
      // という対応を行う
      fullWidth = false,
    },
    ref
  ) => {
    const isNative = Capacitor.isNativePlatform()
    const isNativeIOS = isNative && isIOS
    const {
      router: { pathname },
    } = useRouter()

    useSafeAreaSetting(prevState => merge(prevState, CONSOLE_HEADER))

    const [bgStyle, setBgStyle] = useState<SxProps>({})
    const [bottomMargin, setBottomMarginHeight] = useState(0)

    const { router } = useRouter()
    const contentRef = useRef<HTMLDivElement>()
    const windowHeight = useWindowHeight()

    // 余白の大きさを決定する
    useEffect(() => {
      if ((!customNavigation && !hasConsoleFooter) || !contentRef.current) {
        setBottomMarginHeight(0)
        return
      }
      const footerElm = contentRef.current.nextSibling as HTMLDivElement
      const footerHeight = footerElm?.offsetHeight ?? 0
      const scrollHeight = contentRef.current.scrollHeight ?? 0

      if (
        (!footerElm?.classList.contains('bottomNavigation') &&
          !footerElm?.classList.contains('customBottomNavigation') &&
          !footerElm?.classList.contains('consoleFooter')) ||
        (scrollHeight ?? 0) <= windowHeight - footerHeight
      ) {
        setBottomMarginHeight(0)
      } else {
        setBottomMarginHeight(footerElm?.offsetHeight ?? 0)
      }
    }, [customNavigation, contentRef.current])

    // アーティストの背景色、またはsvgを設定する
    useEffect(() => {
      setBgStyle({
        [isNativeIOS
          ? '.artist-theme-provider & .BgImage'
          : '.artist-theme-provider &']: {
          bgcolor: black ? '#222' : 'background.default',
        },
      })
    }, [router.pathname, black])

    const desktopSideMenuWidth = constants.ui.layout.desktopSideMenuWidth
    const width = useWindowWidth()

    return (
      <Box sx={{ height: '100%' }}>
        {/* 画面幅 md 以上の時に常時表示する PC 用サイドメニュー */}
        <ArtistSideMenuForPC onClose={() => {}} />
        <Box
          ref={ref}
          className={'BaseLayout Console'}
          sx={{
            position: 'relative',
            zIndex: 1,
            pt: safeArea?.pt ?? styles.safeAreaTop,
            pb: safeArea?.pb ?? styles.safeAreaBottom,
            minHeight: `calc(100dvh - ${
              safeArea?.pt ?? styles.safeAreaTop
            }px - ${
              safeArea?.pb ?? styles.safeAreaBottom
            } - ${bottomMargin}px - ${header ? '56px' : '0px'})`,
            overflowX: isNativeIOS ? undefined : 'hidden',
            height: '100%',
            width: { md: `calc(100% - ${desktopSideMenuWidth}px)` },
            ml: { md: `${desktopSideMenuWidth}px` },
            ...(styleSets.hideScrollbar() as {}),
            ...(bgStyle as {}),
            ...sx,
          }}
        >
          {header && (
            <ConsoleHeader
              {...{
                ...header,
                color: 'white',
                bgcolor: 'primary.main',
                position: 'fixed',
              }}
            />
          )}
          <Box
            className="BaseLayoutContent"
            ref={contentRef}
            sx={{
              ...(showScrollBar ? {} : styleSets.hideScrollbar()),
              // レスポンシブ対応が済んでいない場合は最大幅 600px に限定する
              // 全コンソール画面のレスポンシブ対応が完了したら条件分岐を削除し
              // maxWidth: { lg: defaultTheme.breakpoints?.values?.lg ?? 1440 }
              // という指定に変える
              maxWidth: fullWidth
                ? { lg: defaultTheme.breakpoints?.values?.lg ?? 1440 }
                : 600,
              margin: '0 auto',
              width: { sx: '100%', md: width - desktopSideMenuWidth },
              height: '100%',
              px: margin === true ? 5 : +(margin ?? 0),
              bgcolor: black ? '#222' : 'transparent',
              pt: header ? '56px' : 0,
              minHeight: 'auto',
              '&:focus': {
                border: 'none',
                outline: 'none',
              },
            }}
          >
            {children}
            {/* ボトムナビゲーションのための余白要素 */}
            <Box
              className="bottomMargin"
              sx={{
                height: bottomMargin ?? 0,
                width: '100%',
              }}
            />
          </Box>

          {!!customNavigation && (
            <Box
              className="customBottomNavigation"
              sx={{
                position: 'fixed',
                bottom: 0,
                left: {
                  xs: 0,
                  // PC 表示の場合はサイドメニューの幅分左に間隔をとる
                  md: `${desktopSideMenuWidth}px`,
                },
                right: 0,
                width: {
                  xs: '100%',
                  // PC 表示の場合はサイドメニューの幅分幅を小さくする
                  md: `calc(100% - ${desktopSideMenuWidth}px)`,
                },
                zIndex: 10,
                margin: '0 auto',
                bgcolor: 'grey.50',
                pb: styles.safeAreaBottom,
                ...customNavigationSx,
              }}
            >
              {customNavigation}
            </Box>
          )}
          {hasConsoleFooter && (
            <Box
              className="consoleFooter"
              sx={{
                position: 'fixed',
                insetX: 0,
                bottom: 0,
                right: 0,
                left: {
                  xs: 0,
                  // PC 表示の場合はサイドメニューの幅分左に間隔をとる
                  md: `${desktopSideMenuWidth}px`,
                },
                margin: '0 auto',
                width: {
                  xs: '100%',
                  // PC 表示の場合はサイドメニューの幅分幅を小さくする
                  md: `calc(100% - ${desktopSideMenuWidth}px)`,
                },
                zIndex: 10,
                bgcolor: 'background.default',
              }}
            >
              <ConsoleFooter />
            </Box>
          )}
        </Box>
      </Box>
    )
  }
)

export type ConsoleBaseLayoutProps = {
  hasConsoleFooter?: boolean
  header?: HeaderProps
  margin?: boolean | number
  px?: number
  children: ReactNode
  sx?: SxProps<Theme>
  // スクロールを下から行う場合true
  scrollFromBottom?: boolean
  // スクロールバーを表示する
  showScrollBar?: boolean
  safeArea?: {
    pt?: number | string
    pb?: number | string
  }
  // ボトムナビゲーションを自由に設定したい場合はこちら
  customNavigation?: ReactNode
  customNavigationSx?: SxProps
  // コンテンツ背景色を黒にするかどうか
  black?: boolean
  // フル画面表示すなわちレスポンシブ対応ができるかどうか
  // 全てのコンソール画面のレスポンシブ対応が完了したら不要になる
  fullWidth?: boolean
}
